import { useState, useEffect, useCallback } from 'react';
import { notesService } from '../components/services/notesService';

export const useNotesSystem = (documentTitle) => {
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Load notes when document changes
  useEffect(() => {
    if (documentTitle) {
      loadNotes();
    }
  }, [documentTitle]);

  const loadNotes = useCallback(async () => {
    if (!documentTitle) return;
    
    setIsLoading(true);
    try {
      // 1. FIRST: Check localStorage for any unsaved changes
      const localNotes = localStorage.getItem(`notes_${documentTitle}`);
      
      if (localNotes) {
        console.log('📝 Loading notes from localStorage (unsaved changes)');
        setNotes(localNotes);
        setHasLocalChanges(true);
        setLastSaved(null); // Not saved to backend yet
      } else {
        // 2. If no localStorage, fetch from backend
        console.log('📝 Loading notes from backend');
        const notesData = await notesService.getNotes(documentTitle);
        setNotes(notesData.content || '');
        setHasLocalChanges(false);
        setLastSaved(notesData.updatedAt || null);
      }
      
      console.log('✅ Notes loaded successfully');
    } catch (error) {
      console.error('❌ Failed to load notes:', error);
      // Start with empty notes
      setNotes('');
      setHasLocalChanges(false);
    } finally {
      setIsLoading(false);
    }
  }, [documentTitle]);

  const saveNotes = useCallback(async (content) => {
    if (!documentTitle) return false;
    
    setIsSaving(true);
    try {
      // 1. Save to backend
      await notesService.saveNotes(documentTitle, content);
      
      // 2. Clear localStorage after successful backend save
      localStorage.removeItem(`notes_${documentTitle}`);
      
      // 3. Update state
      setNotes(content);
      setHasLocalChanges(false);
      setLastSaved(new Date().toISOString());
      
      console.log('✅ Notes saved to backend successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to save notes to backend:', error);
      
      // Keep notes in localStorage (they're already there)
      setHasLocalChanges(true);
      
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [documentTitle]);

  const updateNotes = useCallback((newNotes) => {
    setNotes(newNotes);
    setHasLocalChanges(true);
    
    // Save to localStorage immediately
    try {
      if (documentTitle) {
        localStorage.setItem(`notes_${documentTitle}`, newNotes);
      }
    } catch (error) {
      console.error('❌ Failed to update localStorage:', error);
    }
  }, [documentTitle]);

  // ✅ ONLY DOWNLOAD FROM BACKEND
  const downloadNotes = useCallback(async () => {
    if (!documentTitle) {
      alert('No document selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log('⬇️ Downloading notes from backend...');
      
      // 1. First fetch the saved notes from backend
      const notesData = await notesService.getNotes(documentTitle);
      const savedNotes = notesData.content || '';
      
      // 2. Check if there are any saved notes
      if (!savedNotes || savedNotes.trim() === '' || 
          savedNotes === '<br>' || savedNotes === '<div><br></div>') {
        alert('No saved notes found. Please save your notes first.');
        return;
      }

      // 3. Download the saved notes
      await notesService.downloadNotes(documentTitle, savedNotes);
      
      console.log('✅ Saved notes downloaded from backend');
      
      // Show warning if local notes differ
      const currentNotes = notes.trim();
      const backendNotes = savedNotes.trim();
      const hasUnsavedChanges = hasLocalChanges || (currentNotes !== backendNotes);
      
      if (hasUnsavedChanges) {
        console.warn('⚠️ Downloaded saved version (different from current notes)');
        // Optionally show a toast notification
      }
      
    } catch (error) {
      console.error('❌ Failed to download notes:', error);
      
      // User-friendly error messages
      if (error.message.includes('401') || error.message.includes('Authentication')) {
        alert('Please log in to download notes.');
      } else if (error.message.includes('404')) {
        alert('No saved notes found. Please save your notes first.');
      } else {
        alert('Failed to download notes. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [documentTitle, notes, hasLocalChanges]);

  const clearNotes = useCallback(() => {
    setNotes('');
    setHasLocalChanges(false);
    setLastSaved(null);
    
    // Clear localStorage as well
    if (documentTitle) {
      localStorage.removeItem(`notes_${documentTitle}`);
    }
  }, [documentTitle]);

  // Force reload from backend (discard local changes)
  const reloadFromBackend = useCallback(async () => {
    if (!documentTitle) return;
    
    setIsLoading(true);
    try {
      // Clear localStorage first
      localStorage.removeItem(`notes_${documentTitle}`);
      
      // Load from backend
      const notesData = await notesService.getNotes(documentTitle);
      setNotes(notesData.content || '');
      setHasLocalChanges(false);
      setLastSaved(notesData.updatedAt || null);
      
      console.log('✅ Notes reloaded from backend');
    } catch (error) {
      console.error('❌ Failed to reload notes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [documentTitle]);

  return {
    notes,
    isLoading,
    isSaving,
    lastSaved,
    hasLocalChanges,
    updateNotes,
    saveNotes,
    downloadNotes, // This now only downloads from backend
    loadNotes,
    clearNotes,
    reloadFromBackend
  };
};